import { CheckIcon, Cross2Icon, Pencil2Icon, TrashIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';

import { SelectModel } from '../../patterns/select/select-model';
import { SwitchCard } from '../../patterns/switch/switch-card';
import { cn } from '../../utils/cn';
import { Values } from '../../utils/types';
import { Badge } from '../badge';
import { Button } from '../button';
import { FormMessage } from '../form';
import { Input } from '../input';
import { InputNumber } from '../input-number';
import { LoadingAddon } from '../loading-addon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';

const TYPE = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
};

type Type = Values<typeof TYPE>;

const TYPES = [
  {
    item: 'string',
    value: TYPE.string,
  },
  {
    item: 'number',
    value: TYPE.number,
  },
  {
    item: 'boolean',
    value: TYPE.boolean,
  },
];

interface DictionaryProps {
  disabled?: boolean;
  id?: string;
  isLoading?: boolean;
  value?: Record<string, string | number | boolean>;
  onChange?: (value: Record<string, string | number | boolean>) => void;
}

export function Dictionary({
  disabled = false,
  id,
  isLoading = false,
  value,
  onChange,
}: DictionaryProps) {
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [data, setData] = useState<Map<string, string | number | boolean>>(
    new Map(Object.entries(value ?? {})),
  );

  useEffect(() => {
    setData(new Map(Object.entries(value ?? {})));
  }, [value]);

  return (
    <div id={id} className="relative">
      <ul
        className={cn('grid gap-1', {
          'mb-1': data.size > 0,
        })}
      >
        {[...data.entries()].map(([key, value]) => {
          if (editItem && editItem.keyProp === key) {
            return (
              <li key={key}>
                <EditForm
                  id={`${id}-${key}`}
                  data={data}
                  type="edit"
                  keyProp={key}
                  valueProp={value}
                  onSubmit={({ keyProp, valueProp }) => {
                    setData((prev) => {
                      const updated = new Map<string, string | number | boolean>();
                      for (const [k, v] of prev.entries()) {
                        if (k === key) {
                          updated.set(keyProp, valueProp);
                        } else {
                          updated.set(k, v);
                        }
                      }

                      onChange?.(Object.fromEntries(updated));
                      return updated;
                    });
                    setEditItem(null);
                  }}
                  onCancel={() => {
                    setEditItem(null);
                  }}
                />
              </li>
            );
          }

          return (
            <li
              key={key}
              className={cn(
                'flex flex-wrap items-end justify-between gap-2 rounded-3xl border p-2.5 pl-4',
                {
                  'hover:bg-accent hover:text-accent-foreground': !disabled && !isLoading,
                  'cursor-not-allowed border-input/50 select-none': disabled || isLoading,
                },
              )}
            >
              <div className="flex flex-1 flex-wrap gap-4 self-center text-base">
                <Badge className="w-[4rem] flex-none justify-center" variant="secondary">
                  {typeof value}
                </Badge>
                <span className="font-medium break-all">{key}:</span>
                <span className="break-all">{String(value)}</span>
              </div>
              <div className="flex">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        id={`${id}-${key}-edit`}
                        variant="outline"
                        size="icon"
                        onClick={() => setEditItem({ keyProp: key, valueProp: value })}
                      >
                        <Pencil2Icon className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                  <div className="ml-2 border-l-[1px] border-input pl-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          id={`${id}-${key}-delete`}
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setData((prev) => {
                              const updated = new Map(prev);
                              updated.delete(key);

                              onChange?.(Object.fromEntries(updated));
                              return updated;
                            });
                          }}
                        >
                          <TrashIcon className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Eliminar</TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>
            </li>
          );
        })}
      </ul>
      <EditForm
        id={`${id}-newItem`}
        data={data}
        disabled={!!editItem || disabled || isLoading}
        onSubmit={({ keyProp, valueProp }) => {
          setData((prev) => {
            const updated = new Map(prev);
            updated.set(keyProp, valueProp);

            onChange?.(Object.fromEntries(updated));
            return updated;
          });
        }}
      />
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
}

interface Item {
  keyProp: string;
  valueProp: string | number | boolean;
}

interface EditFormProps extends Partial<Item> {
  data: Map<string, string | number | boolean>;
  disabled?: boolean;
  id: string;
  type?: 'create' | 'edit';
  onSubmit: (item: Item) => void;
  onCancel?: VoidFunction;
}

function EditForm({
  data = new Map(),
  disabled = false,
  id,
  type = 'create',
  keyProp = '',
  valueProp = '',
  onSubmit,
  onCancel = () => {},
}: EditFormProps) {
  const [inputType, setInputType] = useState<Type>(
    typeof valueProp === 'string'
      ? TYPE.string
      : typeof valueProp === 'number'
        ? TYPE.number
        : TYPE.boolean,
  );
  const [inputKey, setInputKey] = useState<string>(keyProp);
  const [inputValue, setInputValue] = useState<string | number | boolean | undefined>(valueProp);

  useEffect(() => {
    if (inputType === TYPE.string) {
      setInputValue(typeof valueProp === 'string' ? valueProp : '');
    } else if (inputType === TYPE.number) {
      setInputValue(typeof valueProp === 'number' ? valueProp : undefined);
    } else if (inputType === TYPE.boolean) {
      setInputValue(typeof valueProp === 'boolean' ? valueProp : false);
    }
  }, [inputType, valueProp]);

  const keyExists =
    type === 'create' ? data.has(inputKey) : data.has(inputKey) && inputKey !== keyProp;

  return (
    <fieldset
      className={cn('rounded-3xl border p-2.5 shadow-xs', {
        'cursor-not-allowed border-input/50 select-none': disabled,
      })}
      disabled={disabled}
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="w-[6.5rem]">
            <SelectModel
              id={`${id}-inputType`}
              disabled={disabled}
              model={TYPES}
              onValueChange={(value) => setInputType(value)}
              value={inputType}
            />
          </div>
          <Input
            id={`${id}-inputKey`}
            className="min-w-36 flex-1"
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="clave"
            value={inputKey}
          />
          <div className="min-w-36 flex-1">
            {inputType === TYPE.number ? (
              <InputNumber
                id={`${id}-inputValue`}
                decimalScale={2}
                onValueChange={(value) => setInputValue(value)}
                placeholder="valor (12.34)"
                value={typeof inputValue !== 'number' ? undefined : inputValue}
              />
            ) : inputType === TYPE.boolean ? (
              <SwitchCard
                id={`${id}-inputValue`}
                className="py-0"
                checked={typeof inputValue !== 'boolean' ? false : inputValue}
                label={inputValue === true ? 'true' : 'false'}
                onCheckedChange={(checked) => setInputValue(checked)}
              />
            ) : (
              <Input
                id={`${id}-inputValue`}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="valor"
                value={typeof inputValue !== 'string' ? '' : inputValue}
              />
            )}
          </div>
        </div>
        <div className="flex">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id={`${id}-submit`}
                  disabled={
                    !inputType ||
                    inputKey.trim() === '' ||
                    inputValue === undefined ||
                    (typeof inputValue === 'string' && inputValue.trim() === '') ||
                    (keyExists && inputKey !== keyProp) ||
                    disabled
                  }
                  size="icon"
                  onClick={() => {
                    if (
                      !inputType ||
                      inputKey.trim() === '' ||
                      inputValue === undefined ||
                      (typeof inputValue === 'string' && inputValue.trim() === '') ||
                      (keyExists && inputKey !== keyProp) ||
                      disabled
                    ) {
                      return;
                    }

                    onSubmit({
                      keyProp: inputKey,
                      valueProp: inputValue,
                    });

                    setInputType(TYPE.string);
                    setInputKey('');
                    setInputValue('');
                  }}
                >
                  <CheckIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{type === 'create' ? 'Agregar' : 'Confirmar'}</TooltipContent>
            </Tooltip>

            <div className="ml-2 border-l-[1px] border-input pl-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    id={`${id}-cancel`}
                    variant="destructive"
                    size="icon"
                    onClick={() => {
                      if (type === 'edit') {
                        onCancel();
                      } else {
                        setInputType(TYPE.string);
                        setInputKey('');
                        setInputValue('');
                      }
                    }}
                  >
                    <Cross2Icon className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cancelar</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </div>
      {keyExists ? (
        <FormMessage className="p-2">Ya existe un par con esta clave</FormMessage>
      ) : null}
    </fieldset>
  );
}
